import { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, Send, Star, Check, ShieldCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── I18N ─────────────────────────────────────────────────
const LANGS = [
  { code: 'fr', flag: '🇫🇷', label: 'FR' },
  { code: 'en', flag: '🇬🇧', label: 'EN' },
  { code: 'nl', flag: '🇳🇱', label: 'NL' },
];

const T = {
  fr: {
    title: 'Votre avis nous intéresse',
    question: 'Comment évaluez-vous',
    experience: 'votre expérience aujourd\'hui ?',
    happy: 'Très satisfait',
    neutral: 'Moyen',
    sad: 'Pas content',
    redirecting: 'Redirection vers Google...',
    formIntro: 'Merci de partager votre ressenti.',
    formPrivate: 'Votre message reste privé et permet au commerce de s\'améliorer.',
    placeholderMsg: 'Qu\'est-ce qui pourrait être amélioré ?',
    placeholderName: 'Votre prénom (optionnel)',
    placeholderContact: 'Email ou téléphone (optionnel, pour vous répondre)',
    back: 'Retour',
    send: 'Envoyer',
    thanksTitle: 'Merci !',
    thanksMsg: 'Votre message a été transmis à',
    thanksEnd: 'Ils feront leur maximum pour s\'améliorer grâce à votre retour.',
    notFoundTitle: 'Page introuvable',
    notFoundMsg: 'Ce lien n\'existe pas ou a été supprimé.',
    poweredBy: 'Propulsé par Safe Review',
    error: 'Erreur, réessayez',
  },
  en: {
    title: 'We\'d love your feedback',
    question: 'How would you rate',
    experience: 'your experience today?',
    happy: 'Very happy',
    neutral: 'Okay',
    sad: 'Not happy',
    redirecting: 'Redirecting to Google...',
    formIntro: 'Thanks for sharing your feedback.',
    formPrivate: 'Your message stays private and helps the business improve.',
    placeholderMsg: 'What could we improve?',
    placeholderName: 'Your first name (optional)',
    placeholderContact: 'Email or phone (optional, so we can reply)',
    back: 'Back',
    send: 'Send',
    thanksTitle: 'Thank you!',
    thanksMsg: 'Your message has been sent to',
    thanksEnd: 'They\'ll do their best to improve based on your feedback.',
    notFoundTitle: 'Page not found',
    notFoundMsg: 'This link doesn\'t exist or has been removed.',
    poweredBy: 'Powered by Safe Review',
    error: 'Error, please retry',
  },
  nl: {
    title: 'Wij horen graag uw mening',
    question: 'Hoe beoordeelt u',
    experience: 'uw ervaring vandaag?',
    happy: 'Zeer tevreden',
    neutral: 'Gemiddeld',
    sad: 'Niet tevreden',
    redirecting: 'Doorverwijzen naar Google...',
    formIntro: 'Bedankt voor het delen van uw mening.',
    formPrivate: 'Uw bericht blijft privé en helpt de zaak te verbeteren.',
    placeholderMsg: 'Wat kunnen we verbeteren?',
    placeholderName: 'Uw voornaam (optioneel)',
    placeholderContact: 'E-mail of telefoon (optioneel, om te antwoorden)',
    back: 'Terug',
    send: 'Verzenden',
    thanksTitle: 'Bedankt!',
    thanksMsg: 'Uw bericht is verzonden naar',
    thanksEnd: 'Ze zullen hun best doen om dankzij uw feedback te verbeteren.',
    notFoundTitle: 'Pagina niet gevonden',
    notFoundMsg: 'Deze link bestaat niet of is verwijderd.',
    poweredBy: 'Aangedreven door Safe Review',
    error: 'Fout, probeer opnieuw',
  },
};

// ─── Helpers ──────────────────────────────────────────────
function hexToRgba(hex, alpha) {
  const clean = /^#?[0-9a-fA-F]{6}$/.test(hex || '') ? hex.replace('#', '') : 'F59E0B';
  const r = parseInt(clean.substring(0, 2), 16);
  const g = parseInt(clean.substring(2, 4), 16);
  const b = parseInt(clean.substring(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

// ─── MAIN ─────────────────────────────────────────────────
export default function PublicReviewPage() {
  const { slug } = useParams();
  const [filter, setFilter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [mood, setMood] = useState(null);
  const [step, setStep] = useState('choose');
  const [message, setMessage] = useState('');
  const [name, setName] = useState('');
  const [contact, setContact] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Detect browser lang default
  const initialLang = useMemo(() => {
    if (typeof navigator === 'undefined') return 'fr';
    const l = (navigator.language || 'fr').toLowerCase().slice(0, 2);
    return ['fr', 'en', 'nl'].includes(l) ? l : 'fr';
  }, []);
  const [lang, setLang] = useState(initialLang);
  const t = T[lang];

  const accent = filter?.accent_color || '#F59E0B';

  const MOODS = [
    { id: 'happy', emoji: '😊', label: t.happy },
    { id: 'neutral', emoji: '😐', label: t.neutral },
    { id: 'sad', emoji: '😞', label: t.sad },
  ];

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/avis/${slug}`);
        if (!r.ok) { setNotFound(true); return; }
        setFilter(await r.json());
      } catch (e) { setNotFound(true); }
      finally { setLoading(false); }
    })();
  }, [slug]);

  const pickMood = (m) => {
    setMood(m);
    if (m.id === 'happy') {
      // Track click then redirect (only allow http/https to prevent javascript: injection)
      fetch(`${API}/avis/${slug}/click-google`, { method: 'POST' }).catch(() => {});
      setTimeout(() => {
        try {
          const target = new URL(filter.google_review_url);
          if (target.protocol === 'http:' || target.protocol === 'https:') {
            window.location.href = target.href;
          }
        } catch { /* invalid URL, do nothing */ }
      }, 700);
    } else {
      setStep('form');
    }
  };

  const submit = async () => {
    if (!mood || mood.id === 'happy') return;
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/avis/${slug}/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mood: mood.id, message: message.trim(), name: name.trim(), contact: contact.trim() }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setStep('thanks');
    } catch (e) { alert(t.error); }
    finally { setSubmitting(false); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)' }}>
      <Loader2 className="w-8 h-8 text-emerald-400 animate-spin" />
    </div>
  );

  if (notFound) return (
    <div className="min-h-screen flex items-center justify-center px-6 text-center" style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)' }}>
      <div>
        <p className="text-6xl mb-4">❓</p>
        <h1 className="text-2xl font-black text-white mb-2" style={{ fontFamily: 'Outfit' }}>{T[lang].notFoundTitle}</h1>
        <p className="text-slate-400">{T[lang].notFoundMsg}</p>
      </div>
    </div>
  );

  const bg = `linear-gradient(135deg, ${hexToRgba(accent, 0.15)} 0%, ${hexToRgba(accent, 0.35)} 40%, ${hexToRgba(accent, 0.55)} 100%), #FDF7E4`;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 py-10 relative overflow-hidden" style={{ background: bg }}>
      {/* Decorative blobs */}
      <div className="absolute -top-20 -left-20 w-80 h-80 rounded-full opacity-30 blur-3xl" style={{ background: accent }} />
      <div className="absolute -bottom-20 -right-20 w-80 h-80 rounded-full opacity-25 blur-3xl" style={{ background: '#EC4899' }} />

      {/* Language selector */}
      <div className="absolute top-4 right-4 flex gap-1.5 z-10 bg-white/70 backdrop-blur-sm rounded-full p-1" data-testid="lang-selector">
        {LANGS.map(l => (
          <button
            key={l.code}
            onClick={() => setLang(l.code)}
            className={`w-9 h-9 rounded-full flex items-center justify-center text-lg transition-all ${lang === l.code ? 'bg-white shadow-md scale-110' : 'opacity-60 hover:opacity-100'}`}
            title={l.label}
            data-testid={`lang-${l.code}`}
          >
            {l.flag}
          </button>
        ))}
      </div>

      <div className="relative w-full max-w-md">
        {/* Business header */}
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-8">
          {filter.logo_url ? (
            <img src={filter.logo_url} alt={filter.business_name} className="w-20 h-20 rounded-2xl mx-auto mb-3 object-cover shadow-lg" />
          ) : (
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/60 backdrop-blur-sm mb-3">
              {[1, 2, 3, 4, 5].map(i => (
                <Star key={i} className="w-3 h-3 fill-current" style={{ color: accent }} />
              ))}
            </div>
          )}
          <h1 className="text-3xl sm:text-4xl font-black text-slate-900 mb-2" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="biz-name">
            {filter.business_name}
          </h1>
          <p className="text-sm text-slate-700 font-medium">{t.title}</p>
        </motion.div>

        <AnimatePresence mode="wait">
          {step === 'choose' && (
            <motion.div key="choose" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="bg-white rounded-3xl p-6 sm:p-8 shadow-2xl border border-white/40">
              <p className="text-center text-slate-700 mb-6 text-base leading-relaxed" style={{ fontFamily: 'Outfit' }}>
                {t.question} <br /><span className="font-bold text-slate-900">{t.experience}</span>
              </p>
              <div className="grid grid-cols-3 gap-3">
                {MOODS.map(m => (
                  <motion.button
                    key={m.id}
                    whileHover={{ scale: 1.05, y: -4 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => pickMood(m)}
                    className="flex flex-col items-center gap-2 p-4 rounded-2xl border-2 border-transparent hover:border-current bg-slate-50 hover:bg-white hover:shadow-lg transition-all"
                    style={{ color: accent }}
                    data-testid={`mood-${m.id}`}
                  >
                    <span className="text-5xl">{m.emoji}</span>
                    <span className="text-[11px] font-bold text-slate-700 text-center leading-tight">{m.label}</span>
                  </motion.button>
                ))}
              </div>
              {mood?.id === 'happy' && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-5 text-center text-sm font-semibold flex items-center justify-center gap-2" style={{ color: accent }}>
                  <Loader2 className="w-4 h-4 animate-spin" /> {t.redirecting}
                </motion.div>
              )}
            </motion.div>
          )}

          {step === 'form' && mood && (
            <motion.div key="form" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="bg-white rounded-3xl p-6 sm:p-8 shadow-2xl border border-white/40">
              <div className="text-center mb-5">
                <div className="text-5xl mb-2">{mood.emoji}</div>
                <p className="text-sm text-slate-600" style={{ fontFamily: 'Outfit' }}>
                  {t.formIntro} <br />
                  <span className="font-semibold text-slate-900">{t.formPrivate}</span>
                </p>
              </div>
              <div className="space-y-3">
                <textarea
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  placeholder={t.placeholderMsg}
                  rows={4}
                  maxLength={2000}
                  className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 placeholder-slate-400 outline-none focus:border-slate-500 focus:bg-white text-sm resize-none"
                  style={{ fontFamily: 'Outfit' }}
                  data-testid="feedback-message"
                />
                <input
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder={t.placeholderName}
                  maxLength={100}
                  className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 placeholder-slate-400 outline-none focus:border-slate-500 focus:bg-white text-sm"
                  data-testid="feedback-name"
                />
                <input
                  value={contact}
                  onChange={e => setContact(e.target.value)}
                  placeholder={t.placeholderContact}
                  maxLength={200}
                  className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 placeholder-slate-400 outline-none focus:border-slate-500 focus:bg-white text-sm"
                  data-testid="feedback-contact"
                />
                <div className="flex gap-2 pt-2">
                  <button onClick={() => { setStep('choose'); setMood(null); }} className="flex-1 py-3 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold text-sm transition-all">{t.back}</button>
                  <button onClick={submit} disabled={submitting} className="flex-[2] flex items-center justify-center gap-2 py-3 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-sm transition-all active:scale-95 disabled:opacity-50" data-testid="feedback-submit">
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {t.send}
                  </button>
                </div>
              </div>
            </motion.div>
          )}

          {step === 'thanks' && (
            <motion.div key="thanks" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-3xl p-8 shadow-2xl border border-white/40 text-center">
              <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', delay: 0.1 }} className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <Check className="w-10 h-10 text-emerald-600" />
              </motion.div>
              <h2 className="text-2xl font-black text-slate-900 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>{t.thanksTitle}</h2>
              <p className="text-sm text-slate-600 leading-relaxed" style={{ fontFamily: 'Outfit' }}>
                {t.thanksMsg} <span className="font-bold">{filter.business_name}</span>. <br />
                {t.thanksEnd} 🙏
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        <p className="text-center text-[10px] text-slate-600/70 mt-8 flex items-center justify-center gap-1" style={{ fontFamily: 'Outfit' }}>
          <ShieldCheck className="w-3 h-3" /> {t.poweredBy}
        </p>
      </div>
    </div>
  );
}
